/* eslint-disable @typescript-eslint/restrict-plus-operands */
/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description getInventorySearchFriendlyFilters
 */

import { ROOM_TYPE_GROUP } from '@zcode/nr-utils'
import { chomp } from '@zcode/nr/es/shared/utils'
import * as Utils from '@zcode/nr/es/shared/utils/utils'
import type { Filters } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import { format, subDays } from 'date-fns'
import {
  ALLOWED_SORT_OPTIONS,
  DEFAULT_SORT_OPTION,
  MAX_FILTER_PRICE,
} from '../../constants/filterConstants'
import { logger } from '../../logger'
import type { FilterTypes, Query } from './inventorySearchQueryTypes'

export const getInventorySearchFriendlyFilters = ({
  filters,
  shouldExcludeRoomType = true,
  skipPropertyTypes = false,
  urlOnly = false,
}: {
  filters: FilterTypes
  shouldExcludeRoomType?: boolean
  skipPropertyTypes?: boolean
  urlOnly?: boolean
}) => {
  const excludeMaxPrice = filters.maxPrice === MAX_FILTER_PRICE && !filters.customMaxPrice

  // sort should be null if selected sort is DEFAULT_SORT_OPTION, otherwise sort should be an array
  const sorts =
    filters.sort?.length && ALLOWED_SORT_OPTIONS.has(filters.sort[0])
      ? filters.sort[0] === DEFAULT_SORT_OPTION
        ? null
        : filters.sort[0]
      : undefined

  const maxMonthlyPrice = excludeMaxPrice ? null : filters.maxPrice || null

  // properties that need to be cleaned up before
  const query: Query = {}

  if (filters.url) {
    const [cityState, neighborhoodOrZipcode] = filters.url.split('/')

    if (cityState) {
      query.url = cityState
    }

    if (neighborhoodOrZipcode) {
      const isZipcode = /^\d+$/.test(neighborhoodOrZipcode)

      if (isZipcode) {
        query.zipcode = neighborhoodOrZipcode
      } else {
        query.neighborhood = neighborhoodOrZipcode
      }
    }
  }

  if (filters.excludeGroupIds?.length) {
    query.groupIds = { exclude: filters.excludeGroupIds }
  }

  if (sorts) {
    query.sorts = sorts
  }

  if (filters.nearby) {
    query.nearby = filters.nearby
  }

  if (filters.limit) {
    query.limit = filters.limit
  }

  if (maxMonthlyPrice || filters.minPrice) {
    query.price = {
      max: maxMonthlyPrice,
      min: filters.minPrice ? filters.minPrice : null,
    }
  }

  // Box
  if (filters.maxLat && filters.minLat && filters.maxLng && filters.minLng) {
    query.box = {
      maxLat: chomp(filters.maxLat, 5),
      minLat: chomp(filters.minLat, 5),
      maxLng: chomp(filters.maxLng, 5),
      minLng: chomp(filters.minLng, 5),
    }
  }

  // Sqft
  if (filters.minSquareFeet) {
    query.squareFeet = { min: filters.minSquareFeet }
  }

  // Property Types
  if (filters.propertyTypes?.length && !skipPropertyTypes) {
    query.propertyTypes = {
      include: filters.propertyTypes,
    }
  }

  if (
    !ROOM_TYPE_GROUP.every((type) => filters.propertyTypes?.includes(type)) &&
    shouldExcludeRoomType
  ) {
    query.propertyTypes = {
      ...(query.propertyTypes || {}),
      exclude: ROOM_TYPE_GROUP,
    }
  }

  // Pets
  if (filters.dogs && filters.cats) {
    query.pets = { includeAll: [1], include: [2, 3] }
  } else if (filters.dogs) {
    query.pets = { include: [2, 3] }
  } else if (filters.cats) {
    query.pets = { includeAll: [1] }
  }

  // Bedrooms
  const { minBedroom, maxBedroom } = filters.bedrooms?.reduce(
    (acc: { minBedroom: number | null; maxBedroom: number | null }, current, index) => {
      if (current) {
        acc.maxBedroom = index
        if (acc.minBedroom === null) {
          acc.minBedroom = index
        }
      }
      return acc
    },
    { minBedroom: null, maxBedroom: null }
  ) ?? { minBedroom: null, maxBedroom: null }

  if (isNumber(minBedroom) && isNumber(maxBedroom)) {
    if (!(minBedroom === 0 && maxBedroom === 5)) {
      // We need to set max beds to null when 4+ beds is selected so we're not filtering out 5+ bed listings
      query.bedrooms = {
        max: maxBedroom === 4 || maxBedroom === 5 || minBedroom === 4 ? null : maxBedroom,
        min: minBedroom,
      }
    }
  }

  //   Bathrooms
  if (filters.bathroomsRange?.length) {
    // We need to set max baths to null when 5+ baths is selected so we're not filtering out 6+ bath listings
    const lastVal = filters.bathroomsRange[filters.bathroomsRange.length - 1]
    const maxVal = lastVal === 5 ? null : lastVal
    query.bathrooms = {
      max: maxVal,
      min: filters.bathroomsRange[0],
    }
  }

  // or - features
  if (filters.orFeatures?.length) {
    query.features = {
      includeAll: [...filters.orFeatures],
    }
  }

  if (filters.hasVirtualTours) {
    if (query.features?.includeAll?.length) {
      query.features.includeAll = [...query.features.includeAll, [35, 36]]
    } else {
      query.features = {
        includeAll: [[35, 36]],
      }
    }
  }

  if (filters.features?.length) {
    if (query.features?.includeAll?.length) {
      query.features.includeAll = [...query.features.includeAll, ...filters.features]
    } else {
      query.features = {
        includeAll: [...filters.features],
      }
    }
  }

  // listing Amenities
  if (filters.listingAmenities?.length) {
    query.listingAmenities = {
      includeAll: [...filters.listingAmenities],
    }
  }

  // building Amenities
  if (filters.buildingAmenities?.length) {
    query.buildingAmenities = {
      includeAll: [...filters.buildingAmenities],
    }
  }

  // hasImages
  if (filters.hasImages) {
    query.hasImages = true
  }

  // luxury, cheap
  if (filters.maxPricePerBedroomPercentile) {
    query.pricePerBedroom = { max: filters.maxPricePerBedroomPercentile }
  }

  if (filters.minPricePerBedroomPercentile) {
    query.pricePerBedroom = { min: filters.minPricePerBedroomPercentile }
  }

  // postedToday
  if (filters.maxDays === 1) {
    const today = new Date()
    const dayBefore = subDays(today, 1)
    const formattedDate = format(dayBefore, "yyyy-MM-dd'T'HH:mmXXX")

    query.listedOn = { min: formattedDate }
  }

  // Available now
  if (filters.availableBefore) {
    query.dateAvailable = { max: filters.availableBefore }
    query.hasActiveUnits = true
  }

  // No-fee
  if (filters.noFees) {
    query.leasingFee = { max: 0 }
  }

  // rating
  if (filters.rating?.min || filters.rating?.max) {
    query.rating = filters.rating
  }

  // external rating
  if (filters.externalRating?.min || filters.externalRating?.max) {
    query.externalRating = filters.externalRating
  }

  if (filters.floorplans === 1) {
    query.isBuilding = true
  }

  // allow external like we do in djz
  query.external = true

  // drop url if this query has box
  if (query.box && !urlOnly) {
    const { minLat, maxLat, minLng, maxLng } = query.box

    // safe-guard against maxLat > minLat
    if (maxLat < minLat) {
      query.box.minLat = maxLat
      query.box.maxLat = minLat
    }
    // safe-guard against maxLng < minLng
    if (maxLng < minLng) {
      query.box.minLng = maxLng
      query.box.maxLng = minLng
    }

    if (maxLat === minLat || maxLng === minLng) {
      logger.info('invalid lat/lng in getInventorySearchFriendlyFilters', query)
      if (query.url) {
        delete query.box
      } else {
        const epsilon = 0.05
        if (maxLat === minLat) {
          query.box.minLat -= epsilon
          query.box.maxLat += epsilon
        }
        if (maxLng === minLng) {
          query.box.minLng -= epsilon
          query.box.maxLng += epsilon
        }
        delete query.url
        delete query.neighborhood
      }
    } else {
      delete query.url
      delete query.neighborhood
    }
  } else if (query.box && urlOnly) {
    delete query.box
  }

  if (filters.offset) {
    query.offset = filters.offset
  }

  const filteredQuery: { [key: string]: any } = {}
  Utils.oforEach(query, (v: any, k: string | number) => {
    const isArray = Array.isArray(v)
    if ((isArray && v.length) || (!isArray && v !== null)) {
      filteredQuery[k] = v
    }
  })

  return filteredQuery
}
